/**
 * ENG-5688 (redesigned) — file-swap helpers for `agt impersonate`.
 *
 * Extracted from the original PR #1489's impersonate.ts so the helpers
 * are unit-testable on their own and reusable from both `connect` and
 * `exit`. Invariants preserved verbatim from the CR feedback on #1489:
 *
 *  - Symlink-into-ours check uses realpath + startsWith(
 *    $HOME/.augmented-impersonate/), not substring matching.
 *    Closed PR #1489 / CR #3321347799.
 *  - Broken symlinks refuse explicitly rather than fall through as
 *    "didnt-exist". Same CR.
 *  - Pre-existing .pre-impersonate-backup at the target refuses; manual
 *    cleanup needed rather than silent overwrite.
 *  - restoreSwapped won't clobber a non-symlink replacement at the
 *    project path — if the operator (or some external process) put a
 *    real file there during the session, leave it alone.
 */

import {
  existsSync,
  lstatSync,
  readlinkSync,
  realpathSync,
  renameSync,
  symlinkSync,
  unlinkSync,
} from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join, parse, sep } from 'node:path';

import type { BackupKind } from './impersonate-state.js';

export const BACKUP_SUFFIX = '.pre-impersonate-backup';

/**
 * ENG-5756 — find the git working tree `startDir` lives in, if any.
 *
 * Walks `startDir` and its ancestors looking for a `.git` entry; returns
 * the first directory that has one, or `null` if we reach the filesystem
 * root without finding one. `.git` is matched as EITHER a directory
 * (normal clone) OR a file (git worktree / submodule, where `.git` is a
 * gitlink pointing at the real gitdir) — both mean "inside a working
 * tree", which is exactly what `connect` must refuse to swap persona
 * files into (it would clobber the repo's own CLAUDE.md / .mcp.json).
 *
 * This is the dependency-free equivalent of
 * `git rev-parse --show-toplevel` / `--is-inside-work-tree`: no `git`
 * binary required (so it works in the clean shells impersonation
 * targets — ENG-5688 AC4) and trivially unit-testable with temp dirs.
 */
export function findGitWorkTreeRoot(startDir: string): string | null {
  const { root } = parse(startDir);
  let dir = startDir;
  for (;;) {
    if (existsSync(join(dir, '.git'))) return dir;
    if (dir === root) return null;
    const parent = dirname(dir);
    // dirname() is a fixpoint at the root on every platform; guard
    // against an infinite loop if `root` detection ever disagrees.
    if (parent === dir) return null;
    dir = parent;
  }
}

/**
 * Replace the file at `projectPath` with a symlink to `personaPath`,
 * preserving whatever was there before so `exit` can restore it.
 *
 * Cases:
 *  - Regular file → rename to <path>.pre-impersonate-backup, then
 *    create symlink. Restore = rename back.
 *  - Symlink resolving INTO our persona root → remove silently and
 *    re-link (stale prior impersonation that wasn't exited).
 *    Restore = unlink the new symlink.
 *  - Symlink resolving OUTSIDE our persona root → REFUSE; the operator
 *    may have set it up deliberately.
 *  - Broken symlink → REFUSE; treating as absent would silently
 *    overwrite what the operator put there.
 *  - Nothing there → just create the symlink. Restore = unlink.
 */
export function swapInPersonaFile(
  projectPath: string,
  personaPath: string,
): BackupKind {
  let kind: BackupKind;

  if (!lstatSafe(projectPath)) {
    // Truly absent.
    kind = 'didnt-exist';
  } else {
    const stat = lstatSync(projectPath);
    if (stat.isSymbolicLink()) {
      const target = readlinkSync(projectPath);
      // Resolve BOTH sides through realpathSync so macOS's /var ↔
      // /private/var indirection (and any other OS-level symlinks in
      // the path to $HOME) doesn't make a legitimate "ours" target
      // look foreign because of an unequal-but-equivalent prefix.
      const ourRoot =
        realpathSync(homedir()) + sep + '.augmented-impersonate' + sep;
      let resolvedTarget: string;
      try {
        resolvedTarget = realpathSync(projectPath);
      } catch {
        // realpath fails on broken symlinks. Treat as unknown and refuse —
        // silently treating as absent would overwrite what the operator
        // put there.
        throw new Error(
          `${projectPath} is a broken symlink (target ${target}). ` +
            `Resolve manually and retry.`,
        );
      }
      if (resolvedTarget.startsWith(ourRoot)) {
        unlinkSync(projectPath);
        kind = 'was-symlink';
      } else {
        throw new Error(
          `${projectPath} is a symlink to ${target} (resolved: ${resolvedTarget}). ` +
            `Refusing to replace it — it doesn't live under ${ourRoot}. ` +
            `Resolve manually and retry.`,
        );
      }
    } else {
      const backupPath = projectPath + BACKUP_SUFFIX;
      if (existsSync(backupPath)) {
        // Backup already exists from a previous failed run. Refuse rather
        // than silently overwrite — manual cleanup is safer than wrong
        // restore later.
        throw new Error(
          `${backupPath} already exists from a previous run. ` +
            `Remove it manually if you're sure it's stale, then retry.`,
        );
      }
      renameSync(projectPath, backupPath);
      kind = 'had-file';
    }
  }

  symlinkSync(personaPath, projectPath, 'file');
  return kind;
}

/**
 * Reverse of swapInPersonaFile.
 *
 * Removes the symlink at projectPath (if it's still a symlink — leave
 * non-symlink replacements alone) and restores the backup file when
 * one exists.
 */
export function restoreSwapped(projectPath: string, kind: BackupKind): void {
  if (lstatSafe(projectPath)) {
    const stat = lstatSync(projectPath);
    if (stat.isSymbolicLink()) {
      unlinkSync(projectPath);
    } else {
      // Not a symlink — someone replaced it during the session. Don't
      // touch it.
      return;
    }
  }

  if (kind === 'had-file') {
    const backupPath = projectPath + BACKUP_SUFFIX;
    if (existsSync(backupPath)) {
      renameSync(backupPath, projectPath);
    }
  }
  // 'was-symlink' and 'didnt-exist': nothing to restore.
}

function lstatSafe(path: string): boolean {
  try {
    lstatSync(path);
    return true;
  } catch {
    return false;
  }
}
